import { Injectable } from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import { RxState } from '@rx-angular/state';
import { StylesInterface as StyleMicroserviceStrings } from '@vendasta/forms_microservice';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { StyleChanges } from '../../interface';
import { newColorControl } from './color-picker.component';
import { newPixelControl, newPixelUnitControl } from './pixel-size.component';

@Injectable()
export class StylesService {
  form = this.fb.group({
    // Container
    Width: newPixelControl(DefaultFormValues.Width, 1, 5000),
    WidthUnit: newPixelUnitControl(DefaultFormValues.WidthUnit),
    BackgroundColor: newColorControl(DefaultFormValues.BackgroundColor),
    BorderColor: newColorControl(DefaultFormValues.BorderColor),
    BorderRadius: newPixelControl(DefaultFormValues.BorderRadius, 0, 500),
    BorderWidth: newPixelControl(DefaultFormValues.BorderWidth, 0, 50),
    BorderStyle: new FormControl<string>(DefaultFormValues.BorderStyle, { nonNullable: true }),
    Padding: newPixelControl(DefaultFormValues.Padding, 1, 100),

    // Font
    PrimaryFontColor: newColorControl(DefaultFormValues.PrimaryFontColor),

    // Button
    ButtonLabel: new FormControl<string>(DefaultFormValues.ButtonLabel, {
      nonNullable: true,
      validators: [],
    }),
    ButtonColor: newColorControl(DefaultFormValues.ButtonColor),
    ButtonFontColor: newColorControl(DefaultFormValues.ButtonFontColor),
  });

  readonly borderStyleOptions = [
    'none',
    'hidden',
    'dotted',
    'dashed',
    'solid',
    'double',
    'groove',
    'ridge',
    'inset',
    'outset',
  ];

  readonly styleChanged$: Observable<StyleChanges> = this.state.select().pipe(
    map((changes: FormStylesChanged) => ({
      changedByUser: changes.setByUser,
      styles: this.formToCss(changes.styles),
    })),
  );
  readonly submitButtonLabel$: Observable<string> = this.state
    .select('styles')
    .pipe(map((styles) => styles.ButtonLabel || 'Submit'));
  readonly stylesStatus$ = this.form.statusChanges.pipe(startWith(this.form.status));

  constructor(
    private readonly fb: FormBuilder,
    private readonly state: RxState<FormStylesChanged>,
  ) {}

  useExistingStyles(styles: StyleMicroserviceStrings): void {
    this.useExisting(styles, this.form.controls.ButtonLabel.value);
  }

  useExistingSubmitButtonLabel(submitButtonLabel: string): void {
    this.useExisting(this.formToCss(this.state.get('styles')), submitButtonLabel);
  }

  private useExisting(styles: StyleMicroserviceStrings, submitButtonLabel: string): void {
    const formStyles = {
      Width: this.pxCssToValue(styles?.width) || DefaultFormValues.Width,
      WidthUnit: this.pxCssToUnit(styles?.width) || DefaultFormValues.WidthUnit,
      BackgroundColor: styles?.backgroundColor || DefaultFormValues.BackgroundColor,
      BorderColor: styles?.borderColor || DefaultFormValues.BorderColor,
      BorderRadius: this.pxCssToValue(styles?.borderRadius) || DefaultFormValues.BorderRadius,
      BorderWidth: this.pxCssToValue(styles?.borderWidth) || DefaultFormValues.BorderWidth,
      BorderStyle: styles?.borderStyle || DefaultFormValues.BorderStyle,
      Padding: this.pxCssToValue(styles?.padding) || DefaultFormValues.Padding,
      PrimaryFontColor: styles?.primaryFontColor || DefaultFormValues.PrimaryFontColor,
      ButtonLabel: submitButtonLabel || DefaultFormValues.ButtonLabel,
      ButtonColor: styles?.primaryColor || DefaultFormValues.ButtonColor,
      ButtonFontColor: styles?.onPrimaryColor || DefaultFormValues.ButtonFontColor,
    };

    this.form.setValue(formStyles, { emitEvent: false });
    this.state.set({
      styles: formStyles,
      setByUser: false,
    });
  }

  handleUserStyleChanges(): void {
    const stream$ = this.form.valueChanges.pipe(
      map((styles: FormStyles) => ({
        styles: styles,
        setByUser: true,
      })),
    );
    this.state.connect(stream$);
  }

  handleUnitChanged(suffix: string) {
    if (suffix == 'px') {
      this.form.controls.Width.setValue(DefaultFormValues.Width);
    } else if (suffix == '%') {
      this.form.controls.Width.setValue(100);
    }
  }

  private pxCssToValue(px?: string): number | null {
    if (!px) {
      return null;
    }
    const matches = px.match(/(\d+)(px|%)/);
    if (!matches || matches.length < 2) {
      return null;
    }
    return parseInt(matches[1], 10);
  }

  private pxCssToUnit(px?: string): string | null {
    if (!px) {
      return null;
    }
    if (px.endsWith('px')) {
      return 'px';
    } else if (px.endsWith('%')) {
      return '%';
    }
    return null;
  }

  private formToCss(form: FormStyles): StyleMicroserviceStrings {
    return {
      width: form.Width?.toString() + form.WidthUnit?.toString(),
      backgroundColor: form.BackgroundColor?.toString(),
      borderColor: form.BorderColor?.toString(),
      borderRadius: form.BorderRadius?.toString() + 'px',
      borderWidth: form.BorderWidth?.toString() + 'px',
      borderStyle: form.BorderStyle?.toString(),
      padding: form.Padding?.toString() + 'px',
      primaryFontColor: form.PrimaryFontColor?.toString(),
      primaryColor: form.ButtonColor?.toString(),
      onPrimaryColor: form.ButtonFontColor?.toString(),
    };
  }
}

interface FormStylesChanged {
  styles: FormStyles;
  setByUser: boolean;
}

interface FormStyles {
  Width?: number;
  WidthUnit?: string;
  BackgroundColor?: string;
  BorderColor?: string;
  BorderRadius?: number;
  BorderWidth?: number;
  BorderStyle?: string;
  Padding?: number;
  PrimaryFontColor?: string;
  ButtonLabel?: string;
  ButtonColor?: string;
  ButtonFontColor?: string;
}

const DefaultFormValues: Required<FormStyles> = {
  Width: 100,
  WidthUnit: '%',
  BackgroundColor: '#ffffff',
  BorderColor: '#000000',
  BorderRadius: 5,
  BorderWidth: 1,
  BorderStyle: 'solid',
  Padding: 20,
  PrimaryFontColor: '#000000',
  ButtonLabel: '',
  ButtonColor: '#1876D2',
  ButtonFontColor: '#ffffff',
};
